import * as React from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import InfluencerVerificationLabel from 'components/influencer-verification-label/InfluencerVerificationLabel';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import Spinner from 'components/spinner/Spinner';

import InfluencerCreatorNetworkListContext from '../../context/InfluencerCreatorNetworkListContext';
import InfluencerCreatorNetworkListChevron from './chevron/InfluencerCreatorNetworkListItemChevron';
import InfluencerCreatorNetworkListItemMetrics from '../../../../creator-modal/metrics/InfluencerCreatorNetworkListItemMetrics';
import InfluencerCreatorNetworkListPopover from '../../../../creator-modal/popover/InfluencerCreatorNetworkListPopover';

import { getCreatorNetworkInstagramUrl, getCreatorNetworkProfileUrl } from 'utils/influencers';

import './influencer-creator-network-list-item.scss';

import type { NetworkGraphNodeRaw } from 'types';

type Props = {
  item: NetworkGraphNodeRaw,
  isConnectionsLoading: boolean
};

const InfluencerCreatorNetworkListItem = (
  {
    item,
    isConnectionsLoading
  }: Props
): JSX.Element => {
  const context = React.useContext(InfluencerCreatorNetworkListContext);
  const isClickable = !isNil(item.profileId);
  const isExpanded = Boolean(
    isClickable
    && item.profileId
    && context?.expandedId === item.profileId
    && !isConnectionsLoading
  );
  const isDisabled = Boolean(
    isClickable
    && item.profileId
    && context?.disabledItems[item.profileId]
  );
  const profileUrl = getCreatorNetworkProfileUrl(item.profileId, item.profileName);

  const itemClasses = classnames('influencer-creator-network-list-item', {
    'influencer-creator-network-list-item--is-clickable': isClickable && !isDisabled,
    'influencer-creator-network-list-item--is-expanded': isExpanded
  });

  const linkClasses = classnames('influencer-creator-network-list-item__link', {
    'influencer-creator-network-list-item__link--is-clickable': isClickable && !isDisabled
  });

  return (
    <div
      className={itemClasses}
      data-profile-id={item.profileId}
      data-is-expanded={isExpanded}
      onClick={isClickable && !isDisabled ? context?.onToggleItem : undefined}
    >
      <div className="influencer-creator-network-list-item__chevron">
        <InfluencerCreatorNetworkListChevron
          isExpanded={isExpanded}
          isClickable={isClickable && !isDisabled}
        />
      </div>
      <div className="influencer-creator-network-list-item__avatar">
        <InfluencerCreatorNetworkListPopover
          channel="instagram"
          identifier="list-item__avatar-popover"
          parentInfluencerName={context?.rootInfluencerName}
          type="follows"
          isCollabaryProfile={!isNil(item.profileId)}
          triggerComponent={(
            <EntityAvatar
              bordered
              openInNewTab
              entity={{
                image: {
                  url: item.profileImage
                }
              }}
              link={profileUrl}
              customSize={{ width: 24, height: 24 }}
              onClick={(event) => { event.stopPropagation(); }}
            />
          )}
        />
      </div>
      <div className="influencer-creator-network-list-item__name">
        <InfluencerCreatorNetworkListPopover
          channel="instagram"
          identifier="list-item__name-popover"
          parentInfluencerName={context?.rootInfluencerName}
          type="follows"
          isCollabaryProfile={!isNil(item.profileId)}
          triggerComponent={(
            <a
              href={profileUrl}
              className={linkClasses}
              rel="noopener noreferrer"
              target="_blank"
              onClick={(event) => { event.stopPropagation(); }}
            >
              {item.profileName}
            </a>
          )}
        />
      </div>
      <div className="influencer-creator-network-list-item__verification-label">
        {
          !isNil(item.isLightweight) && (
            <InfluencerVerificationLabel
              type={item.isLightweight ? 'unverified' : 'verified'}
            />
          )
        }
      </div>
      {
        isConnectionsLoading && (
          <div className="influencer-creator-network-list-item__spinner">
            <Spinner
              position="inline"
              size="tiny"
              color="light"
            />
          </div>
        )
      }
      <InfluencerCreatorNetworkListItemMetrics
        channel="instagram"
        engagementRate={item.engagementRate}
        followers={item.followers}
        channelLink={getCreatorNetworkInstagramUrl(item.profileName)}
      />
    </div>
  );
};

export default InfluencerCreatorNetworkListItem;
